// The user card and the profile menu that opens upward from it (design/kit/components/Sidebar, Menu):
// My profile, Change password, a divider, Sign out.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import type { Api, Me } from '../auth/api';
import { copy } from '../auth/copy';
import type { SignInState } from '../pages/SignInPage';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ChevronsUpDownIcon, LockIcon, LogOutIcon, UserIcon } from './icons';
import { MyProfileDialog } from './MyProfileDialog';
import { Toast } from './Toast';

type Opened = 'profile' | 'password' | null;

type ProfileMenuProps = {
  me: Me;
  api: Api;
  // Starts with the menu or a dialog open; only the tests use these.
  initiallyOpen?: boolean;
  initialDialog?: Opened;
};

export function ProfileMenu({
  me,
  api,
  initiallyOpen = false,
  initialDialog = null,
}: ProfileMenuProps) {
  const { user } = me;
  const [open, setOpen] = useState(initiallyOpen);
  const [dialog, setDialog] = useState<Opened>(initialDialog);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const cardRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  const showToast = (text: string) => setToast({ id: Date.now(), text });

  // The menu closes on Esc and on page scroll; the scrim catches outside clicks.
  useEffect(() => {
    if (!open) return;
    firstItemRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        cardRef.current?.focus();
      }
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const openDialog = (which: Opened) => {
    setOpen(false);
    setDialog(which);
  };
  const closeDialog = useCallback(() => setDialog(null), []);

  async function signOut() {
    if (busy) return;
    setBusy(true);
    const res = await api.signOut();
    setBusy(false);
    if (!res.ok) return;
    navigate('/sign-in', { replace: true, state: { toast: copy.signedOut } satisfies SignInState });
  }

  async function forgetDevice() {
    if (busy) return;
    setBusy(true);
    const res = await api.forgetDevice();
    setBusy(false);
    if (!res.ok) return showToast(copy.somethingWrong);
    // Reload the user so My profile shows the device as not remembered.
    revalidator.revalidate();
    showToast(copy.deviceForgotten(user.name));
  }

  return (
    <div className="ucw">
      <button
        ref={cardRef}
        type="button"
        className="ucard"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar">{user.initials}</span>
        <span className="col g2 grow" style={{ minWidth: 0 }}>
          <span className="t13 w5 ink ell">{user.name}</span>
          <span className="t11 muted ell">{user.role}</span>
        </span>
        <ChevronsUpDownIcon className="ico s14" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="pop-scrim"
            aria-label="Close profile menu"
            tabIndex={-1}
            onClick={() => setOpen(false)}
          ></button>
          <div className="menu pmenu" role="menu" aria-label="Profile">
            <div className="pm-h">
              <span className="avatar">{user.initials}</span>
              <span className="col g4" style={{ minWidth: 0 }}>
                <span className="t13 w6 ink ell">{user.name}</span>
                <span className="t12 muted ell">{user.email}</span>
                <span className="row">
                  <span className="tag t-grey">{user.role}</span>
                </span>
              </span>
            </div>
            <button
              ref={firstItemRef}
              type="button"
              className="mi"
              role="menuitem"
              aria-haspopup="dialog"
              onClick={() => openDialog('profile')}
            >
              <UserIcon />
              {copy.myProfile}
            </button>
            <button
              type="button"
              className="mi"
              role="menuitem"
              aria-haspopup="dialog"
              onClick={() => openDialog('password')}
            >
              <LockIcon />
              {copy.changePassword}
            </button>
            <div className="vm-div"></div>
            <button type="button" className="mi" role="menuitem" disabled={busy} onClick={signOut}>
              <LogOutIcon />
              {copy.signOut}
            </button>
          </div>
        </>
      )}
      {dialog === 'profile' && (
        <MyProfileDialog
          me={me}
          onClose={closeDialog}
          onForgetDevice={forgetDevice}
          forgetting={busy}
        />
      )}
      {dialog === 'password' && (
        <ChangePasswordDialog
          api={api}
          email={user.email}
          onClose={closeDialog}
          onChanged={() => {
            setDialog(null);
            showToast(copy.passwordChanged);
          }}
        />
      )}
      {toast && <Toast key={toast.id} text={toast.text} />}
    </div>
  );
}
